// Entry point for the docs-site samples bundle. Each import + register
// call here makes one `<ppp-sample-*>` custom element available to the
// Blazor demo host once `samples.js` has been loaded by App.razor.
//
// Adding a new sample is a two-step change: write the .tsx file under
// samples/<area>/, then add a register call below. The .tsx itself stays
// clean (no provider envelope, no registration) so the source tab on the
// docs page shows exactly what a consumer would paste into their own app.

import { defineSampleElement } from './define-sample-element';
import { BoolEditMainSample } from './editors/BoolEditMainSample';
import { BoolEditPropertiesSample } from './editors/BoolEditPropertiesSample';
import { ChoiceEditMainSample } from './editors/ChoiceEditMainSample';
import { ChoiceEditPropertiesSample } from './editors/ChoiceEditPropertiesSample';
import { DateEditMainSample } from './editors/DateEditMainSample';
import { DateEditPropertiesSample } from './editors/DateEditPropertiesSample';
import { FileEditMainSample } from './editors/FileEditMainSample';
import { FileEditPropertiesSample } from './editors/FileEditPropertiesSample';
import { ImageEditMainSample } from './editors/ImageEditMainSample';
import { ImageEditPropertiesSample } from './editors/ImageEditPropertiesSample';
import { LookupEditMainSample } from './editors/LookupEditMainSample';
import { LookupEditPropertiesSample } from './editors/LookupEditPropertiesSample';
import { MemoEditMainSample } from './editors/MemoEditMainSample';
import { MemoEditPropertiesSample } from './editors/MemoEditPropertiesSample';
import { MoneyEditMainSample } from './editors/MoneyEditMainSample';
import { MoneyEditPropertiesSample } from './editors/MoneyEditPropertiesSample';
import { MultiSelectChoiceEditMainSample } from './editors/MultiSelectChoiceEditMainSample';
import { MultiSelectChoiceEditPropertiesSample } from './editors/MultiSelectChoiceEditPropertiesSample';
import { NumberEditMainSample } from './editors/NumberEditMainSample';
import { NumberEditPropertiesSample } from './editors/NumberEditPropertiesSample';
import { TextEditMainSample } from './editors/TextEditMainSample';
import { TextEditMaskSample } from './editors/TextEditMaskSample';
import { TextEditPropertiesSample } from './editors/TextEditPropertiesSample';
import { LookupRecordContextMainSample } from './contexts/LookupRecordContextMainSample';
import { MainContextMainSample } from './contexts/MainContextMainSample';
import { RecordContextMainSample } from './contexts/RecordContextMainSample';
import { BarChartDataverseSample } from './charts/BarChartDataverseSample';
import { BarChartLinkedEntitySample } from './charts/BarChartLinkedEntitySample';
import { BarChartMainSample } from './charts/BarChartMainSample';
import { BarChartViewSample } from './charts/BarChartViewSample';
import { DoughnutChartMainSample } from './charts/DoughnutChartMainSample';
import { FunnelChartMainSample } from './charts/FunnelChartMainSample';
import { LineChartMainSample } from './charts/LineChartMainSample';
import { LineChartYoYSample } from './charts/LineChartYoYSample';
import { PieChartMainSample } from './charts/PieChartMainSample';
import { PolarAreaChartMainSample } from './charts/PolarAreaChartMainSample';
import { RadarChartMainSample } from './charts/RadarChartMainSample';
import { FileGridMainSample } from './grids/FileGridMainSample';
import { GridColumnsMainSample } from './grids/GridColumnsMainSample';
import { GridEditingMainSample } from './grids/GridEditingMainSample';
import { MainGridMainSample } from './grids/MainGridMainSample';
import { MainGridVirtualizeSample } from './grids/MainGridVirtualizeSample';
import { ManyToManyLookupEditorMainSample } from './grids/ManyToManyLookupEditorMainSample';
import { ManyToManyLookupEditorPropertiesSample } from './grids/ManyToManyLookupEditorPropertiesSample';
import { SubGridMainSample } from './grids/SubGridMainSample';
import { SubGridManyToManySample } from './grids/SubGridManyToManySample';
import { DialogServiceMainSample } from './utilities/DialogServiceMainSample';
import { FileViewerImageSample } from './viewers/FileViewerImageSample';
import { FileViewerMarkdownSample } from './viewers/FileViewerMarkdownSample';
import { FileViewerTextSample } from './viewers/FileViewerTextSample';
import { FileViewerUnsupportedSample } from './viewers/FileViewerUnsupportedSample';
import { ImageViewerBareSample } from './viewers/ImageViewerBareSample';
import { ImageViewerCroppableSample } from './viewers/ImageViewerCroppableSample';
import { ImageViewerFramedSample } from './viewers/ImageViewerFramedSample';
import { ImageViewerToolbarSample } from './viewers/ImageViewerToolbarSample';
import { PageLayoutMainSample } from './layout/PageLayoutMainSample';
import { PageLayoutPropertiesSample } from './layout/PageLayoutPropertiesSample';
import { SectionColumnWidthsSample } from './layout/SectionColumnWidthsSample';
import { SectionMainSample } from './layout/SectionMainSample';
import { SectionPropertiesSample } from './layout/SectionPropertiesSample';

// `recordContext` is provided here (not in the sample files) so the source
// tab shows just the component being demonstrated. The wrapper supplies the
// surrounding `<RecordContext>` so the column-bound editors have somewhere
// to bind. Same table for all — ppp_democustomtable is a demo table with
// `ppp_name` (text) and `ppp_credithold` (bool) columns for parameter
// exploration.
defineSampleElement('ppp-sample-textedit-main', TextEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-textedit-properties', TextEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-textedit-mask', TextEditMaskSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-booledit-main', BoolEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-booledit-properties', BoolEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-choiceedit-main', ChoiceEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-choiceedit-properties', ChoiceEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-dateedit-main', DateEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-dateedit-properties', DateEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-fileedit-main', FileEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-fileedit-properties', FileEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-imageedit-main', ImageEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-imageedit-properties', ImageEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-lookupedit-main', LookupEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-lookupedit-properties', LookupEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-memoedit-main', MemoEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-memoedit-properties', MemoEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-moneyedit-main', MoneyEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-moneyedit-properties', MoneyEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-multiselectchoiceedit-main', MultiSelectChoiceEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement(
  'ppp-sample-multiselectchoiceedit-properties',
  MultiSelectChoiceEditPropertiesSample,
  {
    recordContext: { table: 'ppp_democustomtable' },
  },
);
defineSampleElement('ppp-sample-numberedit-main', NumberEditMainSample, {
  recordContext: { table: 'ppp_democustomtable' },
});
defineSampleElement('ppp-sample-numberedit-properties', NumberEditPropertiesSample, {
  recordContext: { table: 'ppp_democustomtable' },
});

// Layout samples — PageLayout and Section both use editors inside, so they
// still need a surrounding RecordContext envelope from the host.
defineSampleElement('ppp-sample-pagelayout-main', PageLayoutMainSample, {
  recordContext: { table: 'account' },
});
defineSampleElement('ppp-sample-pagelayout-properties', PageLayoutPropertiesSample, {
  recordContext: { table: 'account' },
});
defineSampleElement('ppp-sample-section-main', SectionMainSample, {
  recordContext: { table: 'account' },
});
defineSampleElement('ppp-sample-section-properties', SectionPropertiesSample, {
  recordContext: { table: 'account' },
});
defineSampleElement('ppp-sample-section-column-widths', SectionColumnWidthsSample, {
  recordContext: { table: 'account' },
});

// Context samples — the sample's job is to SHOW the context wrapper in
// the source, so each TSX renders its own MainContext / RecordContext /
// LookupRecordContext directly. We deliberately omit `recordContext`
// from the host envelope here so it doesn't double-wrap the sample with
// an outer RecordContext the source tab wouldn't reflect.
defineSampleElement('ppp-sample-maincontext-main', MainContextMainSample);
defineSampleElement('ppp-sample-recordcontext-main', RecordContextMainSample);
defineSampleElement(
  'ppp-sample-lookuprecordcontext-main',
  LookupRecordContextMainSample,
);

// Grid samples — same reasoning as context samples: the FileGrid sample
// owns its own `<MainContext>` for the Save / Refresh toolbar, so no
// outer recordContext envelope. (Future grid samples that drop into a
// pre-bound RecordContext can add it back via the options arg.)
defineSampleElement('ppp-sample-filegrid-main', FileGridMainSample);

// SubGrid samples live inside the parent record's `<RecordContext>` —
// same shape the Blazor demo page wraps everything in. Pin to the
// same account GUID so both stacks read the same related rows.
//
// `skipFetch: true` seeds the RecordContext with a stub `{ tableName, id }`
// record so the envelope doesn't fire a server fetch for the parent
// account. SubGrid only needs the parent reference (table + id) as a
// relationship filter key — it never reads the parent's column values —
// and a fetch failure on the parent would leave the sample area blank
// instead of rendering the related grid. Mirrors how the Blazor demo
// page passes a hand-constructed `new TableRecord { TableName, Id }`
// straight into `<RecordContext Record="..." />` for the same reason.
const SUBGRID_ACCOUNT_ID = 'dbbc9453-ee34-f111-88b4-6045bd02c77d';
defineSampleElement('ppp-sample-subgrid-main', SubGridMainSample, {
  recordContext: { table: 'account', id: SUBGRID_ACCOUNT_ID, skipFetch: true },
});
defineSampleElement('ppp-sample-subgrid-many-to-many', SubGridManyToManySample, {
  recordContext: { table: 'account', id: SUBGRID_ACCOUNT_ID, skipFetch: true },
});

// ManyToManyLookupEditor samples — also live inside an `<RecordContext>`
// (the Blazor demo page wraps in `account` create-mode). No id =
// create-mode record, same as Blazor's `new TableRecord { TableName = "account" }`.
defineSampleElement(
  'ppp-sample-manytomanylookupeditor-main',
  ManyToManyLookupEditorMainSample,
  { recordContext: { table: 'account' } },
);
defineSampleElement(
  'ppp-sample-manytomanylookupeditor-properties',
  ManyToManyLookupEditorPropertiesSample,
  { recordContext: { table: 'account' } },
);

// Chart samples — chart components don't need a RecordContext envelope
// (DataverseChart binds to its own DataSource; FluentUIChart takes
// inline labels/datasets).
defineSampleElement('ppp-sample-barchart-main', BarChartMainSample);
defineSampleElement('ppp-sample-barchart-dataverse', BarChartDataverseSample);
defineSampleElement('ppp-sample-barchart-view', BarChartViewSample);
defineSampleElement('ppp-sample-barchart-linked-entity', BarChartLinkedEntitySample);
defineSampleElement('ppp-sample-piechart-main', PieChartMainSample);
defineSampleElement('ppp-sample-doughnutchart-main', DoughnutChartMainSample);
defineSampleElement('ppp-sample-linechart-main', LineChartMainSample);
defineSampleElement('ppp-sample-linechart-yoy', LineChartYoYSample);
defineSampleElement('ppp-sample-funnelchart-main', FunnelChartMainSample);
defineSampleElement('ppp-sample-radarchart-main', RadarChartMainSample);
defineSampleElement('ppp-sample-polarareachart-main', PolarAreaChartMainSample);

// GridColumns sample renders a MainGrid against the `contact` table —
// no outer RecordContext envelope; the grid owns its own data fetching.
defineSampleElement('ppp-sample-gridcolumns-main', GridColumnsMainSample);
defineSampleElement('ppp-sample-gridediting-main', GridEditingMainSample);

// MainGrid sample — standalone grid over the `contact` table, no outer
// RecordContext envelope (unlike SubGrid). The grid owns its own fetch.
defineSampleElement('ppp-sample-maingrid-main', MainGridMainSample);
// Infinite-scroll (`pagingMode={PagingMode.Virtualize}`) variant of the
// MainGrid demo. Same view shape, smaller chunk size so scrolling
// exercises the accumulator.
defineSampleElement('ppp-sample-maingrid-virtualize', MainGridVirtualizeSample);

// DialogService sample uses the `useDialogService` hook from the
// surrounding FluentDialogProvider (set up in define-sample-element).
// No record context needed; the hook is purely imperative.
defineSampleElement('ppp-sample-dialogservice-main', DialogServiceMainSample);

// Viewer samples are pure presentational — no data dependencies. One ppp-sample-*
// element per Blazor sub-demo block on the FileViewer / ImageViewer pages so each
// `<DemoSection>` lights up its own live React example next to the Blazor one.
defineSampleElement('ppp-sample-fileviewer-image', FileViewerImageSample);
defineSampleElement('ppp-sample-fileviewer-markdown', FileViewerMarkdownSample);
defineSampleElement('ppp-sample-fileviewer-text', FileViewerTextSample);
defineSampleElement('ppp-sample-fileviewer-unsupported', FileViewerUnsupportedSample);
defineSampleElement('ppp-sample-imageviewer-bare', ImageViewerBareSample);
defineSampleElement('ppp-sample-imageviewer-framed', ImageViewerFramedSample);
defineSampleElement('ppp-sample-imageviewer-toolbar', ImageViewerToolbarSample);
defineSampleElement('ppp-sample-imageviewer-croppable', ImageViewerCroppableSample);
